import React, { useState, useEffect } from 'react';
import { Button } from '../common/Button';
import * as api from '../../api/user-app-api';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";


export const OrderDetailScreen = ({ setView, params }) => {
    const [order, setOrder] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [logo, setLogo] = useState(null);
    const [logoLoading, setLogoLoading] = useState(true);
    useEffect(() => {
        const fetchLogo = async () => {
            try {
                setLogoLoading(true);
                const res = await api.fetchSettings();
                if (res.data && res.data.logo_url) {
                    setLogo(res.data.logo_url);
                }
            } catch (e) {
                console.error("Failed to fetch logo:", e);
            } finally {
                setLogoLoading(false);
            }
        };
        fetchLogo();
    }, []);
    useEffect(() => {
        const loadOrder = async () => {
            if (!params?.orderId) {
                setView('orderHistory');
                return;
            }

            try {
                const res = await api.fetchOrderById({ order_id: params.orderId });

                if (res.data.status === 'success') {
                    setOrder(res.data.data);
                } else {
                    setError(res.data.message || 'Order not found.');
                }
            } catch (err) {
                console.error('Error loading order:', err);
                setError('Failed to load order details.');
            } finally {
                setIsLoading(false);
            }
        };

        loadOrder();
    }, [params?.orderId]);

    const getBase64FromUrl = async (url) => {
        const res = await fetch(url);
        const blob = await res.blob();

        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    };

    const downloadPDF = async () => {
        const doc = new jsPDF("p", "mm", "a4");
        let logoBase64 = null;
        const totalAmount = parseFloat(order.total_estimated_value || 0);

        try {
            if (logo) {
                logoBase64 = await getBase64FromUrl(logo);
                doc.addImage(logoBase64, "JPEG", 145, 15, 50, 15);
            }
        } catch (e) {
            console.error("Logo load failed", e);
        }
        doc.setFontSize(18);
        doc.text("Green Certificate", 14, 30);

        doc.setFontSize(10);
        doc.text(`Green Certificate No: ${order.id}`, 14, 40);
        doc.text(`Status: ${order.status}`, 150, 40);
        doc.text(`Created: ${formatDate(order.created_at)}`, 14, 46);

        if (order.status === "Picked Up") {
            doc.text(`Picked: ${formatDate(order.scheduled_date)}`, 14, 52);
        }
        doc.text(`Order Type: ${order.order_type}`, 14, 58);

        doc.text(`Address: ${order.collection_address}`, 14, 64, {
            maxWidth: 180
        });

        const tableHead = [["S.No", "Product", "Qty", "Rate", "Amount"]];

        const tableBody = order.items.map((item, index) => {
            const qty = parseFloat(item.quantity || 0);
            const price = parseFloat(item.price_at_order || 0);
            const subtotal = qty * price;

            return [
                index + 1,
                item.product_name,
                qty,
                `Rs.${price}`,
                `Rs.${subtotal.toFixed(2)}`
            ];
        });

        autoTable(doc, {
            startY: 60,
            head: tableHead,
            body: tableBody,
            theme: "grid",
            styles: { fontSize: 9 },
            headStyles: { fillColor: [1, 164, 153] }
        });

        const finalY = doc.lastAutoTable.finalY + 10;

        if (order.status === "Picked Up") {
            doc.setFontSize(12);
            doc.text(
                `Total Amount: Rs.${totalAmount.toFixed(2)}`,
                140,
                finalY
            );
        }

        doc.save(`Green_Certificate_${order.id}.pdf`);
    };

    const formatDate = (dateStr) => {
    if (!dateStr || dateStr.includes('0000-00-00')) {
        return 'Not Scheduled Yet';
    }

    const date = new Date(dateStr);

    const hasTime = dateStr.includes('T') || dateStr.includes(':');

    if (hasTime) {
        return date.toLocaleString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    }

    return date.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
};

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'pending':
                return 'text-yellow-700 bg-yellow-100';
            case 'scheduled':
                return 'text-blue-700 bg-blue-100';
            case 'picked up':
                return 'text-[#01a499] bg-[#01a499]/10';
            case 'cancelled':
                return 'text-red-700 bg-red-100';
            default:
                return 'text-gray-700 bg-gray-100';
        }
    };

    if (isLoading) {
        return <div className="p-4">Loading order details...</div>;
    }

    if (error || !order) {
        return (
            <div className="p-4 text-center">
                <p className="text-gray-600 mb-4">{error || 'Order not found'}</p>
                <Button onClick={() => setView('orderHistory')}>
                    Back to Orders
                </Button>
            </div>
        );
    }

    // ===== Calculations =====
    const totalAmount = parseFloat(order.total_estimated_value || 0);

    const totalQuantity =
        order.items?.reduce(
            (sum, item) => sum + parseFloat(item.quantity || 0),
            0
        ) || 0;

    const totalItems = order.items?.length || 0;

    return (
        <div className="relative h-[calc(100vh-80px)] overflow-y-auto pb-28">
            <div className="p-4 pt-0 mb-16 pb-28">

                <div className="bg-white rounded-xl shadow-lg border border-gray-100 divide-y">
                    <div className='text-center p-4'>
                        <div>
                            <span className="text-m font-semibold text-[#01a499]">
                                GREEN CERTIFICATE
                            </span>
                        </div>
                        <div>
                            <p className="text-sm font-normal text-[#01a499]">
                                You are a Climate Warrior
                            </p>
                        </div>
                    </div>
                    {/* Order Header */}
                    <div className="p-4">
                        <div className="flex justify-between items-start">
                            <div>
                                <p className="font-semibold text-gray-900">
                                    Certificate No: #{order.id}
                                </p>
                                <p className="text-sm text-gray-600">
                                    Order Placed Date: {formatDate(order.created_at)}
                                </p>
                                {order.status == 'Picked Up' && (
                                    <p className="text-sm text-gray-600">
                                        Picked on: {formatDate(order.estimated_date)}
                                    </p>
                                )}
                                {order.status == 'Scheduled' && (
                                    <p className="text-sm text-orange-600">
                                        Scheduled Date: {formatDate(order.estimated_date)}
                                    </p>
                                )}
                                <p className="text-sm text-gray-600">
                                    Order Type: {order.order_type}
                                </p>

                            </div>

                            <span
                                className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                                    order.status
                                )}`}
                            >
                                {order.status}
                            </span>

                        </div>
                        {order.status == 'Pending' && (
                            <div className="mt-2">
                                <p className="text-sm text-yellow-600 text-xs">
                                    Note: Our executive will contact you for scheduling the pickup. Please keep your phone available.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Pickup Details */}
                    <div className="p-4">
                        <h3 className="font-semibold text-gray-800 mb-2">
                            Pickup Details
                        </h3>

                        <p className="text-sm text-gray-600">
                            <span className="font-semibold">
                                {order?.scheduled_route?.name}
                            </span>
                        </p>

                        {order?.scheduled_route?.route && (
                            <p className="text-xs text-gray-500 mt-1">
                                {order.scheduled_route.route}
                            </p>
                        )}

                        {order?.scheduled_route?.link && (
                            <a
                                href={order.scheduled_route.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 underline text-sm mt-1 inline-block"
                            >
                                View Route on Map
                            </a>
                        )}

                        {/* <p className="text-sm text-gray-600 mt-2">
                            Scheduled Date: {formatDate(order.scheduled_date)}
                        </p> */}

                        <p className="text-sm text-gray-800 mt-2">
                            {order.collection_address}
                        </p>
                    </div>

                    {/* Items */}
                    <div className="p-4">
                        <h3 className="font-semibold text-gray-800 mb-3">
                            Items
                        </h3>

                        <div className="space-y-3">
                            {order.items?.map((item, index) => {
                                const quantity = parseFloat(item.quantity);
                                const price = parseFloat(item.price_at_order);
                                const subtotal = quantity * price;

                                return (
                                    <div
                                        key={index}
                                        className="flex justify-between items-start border-b pb-3"
                                    >
                                        <div>
                                            <p className="text-gray-900 font-medium">
                                                {item.product_name}
                                            </p>
                                            {order.status == 'Picked Up' && (
                                                <div>
                                                    {order.order_type == 'SELL' && (
                                                        <p className="text-xs text-gray-600">
                                                            ₹{price} × {quantity}
                                                        </p>)}
                                                </div>
                                            )}

                                        </div>

                                        <div className="text-right">
                                            {order.order_type == 'SELL' ? (
                                                <p className="font-semibold text-gray-800">
                                                    ₹{subtotal.toFixed(2)}
                                                    {order.status != 'Picked Up' && (`/ KG`)}
                                                </p>) : (
                                                <p className="font-semibold text-gray-800">{quantity}</p>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Order Summary */}
                        <div className="mt-6 bg-gray-50 p-4 rounded-xl space-y-2">
                            <h3 className="font-semibold text-gray-800 mb-2">
                                Order Summary
                            </h3>

                            <div className="flex justify-between text-sm">
                                <span>Total Items</span>
                                <span>{totalItems}</span>
                            </div>

                            <div className="flex justify-between text-sm">
                                <span>Total Quantity</span>
                                <span>{totalQuantity}</span>
                            </div>
                            {order.status == 'Picked Up' && (
                                <div>
                                    {order.order_type == 'SELL' && (
                                        <div className="flex justify-between font-semibold text-gray-900 border-t pt-2">
                                            <span>Total Value</span>
                                            <span className="text-[#01a499]">
                                                ₹{totalAmount.toFixed(2)}
                                            </span>
                                        </div>)}
                                </div>
                            )}

                        </div>


                    </div>
                </div>

                {/* Bottom Button */}
                <div className="fixed bottom-16 left-0 right-0 p-4 bg-white border-t pb-7 space-y-2">
                    {order.status == 'Picked Up' && (
                        <Button
                            onClick={downloadPDF}
                            className="!bg-[#01a499]"
                        >
                            Download Green Certificate
                        </Button>
                    )}
                    {order.status?.toLowerCase() === 'cancelled' ? (
                        <Button
                            onClick={() => setView('cart')}
                            className="!bg-[#01a499]"
                        >
                            Create New Order
                        </Button>
                    ) : null}
                    {/* (
                        <Button
                            onClick={() =>
                                setView('help', { orderId: order.id })
                            }
                            className="!bg-gray-600"
                        >
                            Need Help?
                        </Button>
                    )} */}
                </div>

            </div>
        </div>
    );
};
